import React from 'react';
import { MapPin, Phone, Clock, Mail } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useScrollReveal } from '../hooks/useAnimations';

export default function ContactSection() {
  const { t } = useLanguage();
  const [headerRef, headerVisible] = useScrollReveal(0.2);
  const [mapRef, mapVisible] = useScrollReveal(0.1);
  const [detailsRef, detailsVisible] = useScrollReveal(0.1);

  return (
    <section id="contact" className="py-20 md:py-32 bg-white" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div ref={headerRef} className={`text-center mb-16 transition-all duration-700 ${headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <p className="text-sm font-manrope font-medium text-[#D1A760] tracking-wide uppercase mb-3">Best Skin Clinic in Jhansi</p>
          <h2 className="font-outfit font-light text-3xl sm:text-4xl lg:text-5xl text-[#0F172A] tracking-tight" data-testid="contact-title">
            {t('contact_title')}
          </h2>
          <p className="mt-4 text-base text-[#475569] font-manrope max-w-xl mx-auto">
            Skin doctor near Medical College Jhansi. Visit Dr. Kuldeep Verma for expert dermatology and skin care.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Map */}
          <div ref={mapRef} className={`rounded-2xl md:rounded-3xl overflow-hidden shadow-[0_8px_30px_rgb(0,0,0,0.06)] h-[300px] md:h-[400px] transition-all duration-700 ${mapVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`} data-testid="google-map">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3602.5!2d78.57!3d25.44!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjXCsDI2JzI0LjAiTiA3OMKwMzQnMTIuMCJF!5e0!3m2!1sen!2sin!4v1234567890"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Best Dermatologist in Jhansi - Divyadeep Skin Clinic Location Near Medical College"
            />
          </div>

          {/* Contact Details */}
          <div ref={detailsRef} className={`space-y-6 transition-all duration-700 delay-200 ${detailsVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}>
            <div className="bg-[#FAFAFA] rounded-3xl p-8 space-y-6" data-testid="contact-details">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-[#FDF1EC] flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-5 h-5 text-[#D1A760]" />
                </div>
                <div>
                  <p className="font-outfit font-medium text-[#0F172A] text-sm mb-1">Address</p>
                  <p className="text-sm text-[#475569] font-manrope leading-relaxed">{t('contact_address')}</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-[#F0F9FF] flex items-center justify-center flex-shrink-0">
                  <Phone className="w-5 h-5 text-blue-500" />
                </div>
                <div>
                  <p className="font-outfit font-medium text-[#0F172A] text-sm mb-1">{t('contact_phone')}</p>
                  <a href="tel:+918303365990" className="text-sm text-[#475569] font-manrope hover:text-[#D1A760] transition-colors">
                    083033 65990
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-green-50 flex items-center justify-center flex-shrink-0">
                  <Clock className="w-5 h-5 text-green-500" />
                </div>
                <div>
                  <p className="font-outfit font-medium text-[#0F172A] text-sm mb-1">Timings</p>
                  <p className="text-sm text-[#475569] font-manrope">{t('contact_timings')}</p>
                  <p className="text-xs text-[#94A3B8] font-manrope mt-1">Sunday: Closed</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-violet-50 flex items-center justify-center flex-shrink-0">
                  <Mail className="w-5 h-5 text-violet-500" />
                </div>
                <div>
                  <p className="font-outfit font-medium text-[#0F172A] text-sm mb-1">Email</p>
                  <p className="text-sm text-[#475569] font-manrope">divyadeep1904@gmail.com</p>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="grid grid-cols-2 gap-4">
              <a
                href="tel:+918303365990"
                className="flex items-center justify-center gap-2 py-4 rounded-2xl bg-[#0F172A] text-white font-manrope font-medium text-sm hover:bg-[#1E293B] transition-colors"
                data-testid="contact-call-btn"
              >
                <Phone className="w-4 h-4" />
                {t('contact_phone')}
              </a>
              <a
                href="https://wa.me/918303365990"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 py-4 rounded-2xl bg-[#25D366] text-white font-manrope font-medium text-sm hover:bg-[#20BD5C] transition-colors"
                data-testid="contact-whatsapp-btn"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                {t('contact_whatsapp')}
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
